<template>
  <div class="results-page">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <img src="@/assets/Icons/Loading.gif" alt="Loading" class="loading-gif" />
      <p class="loading-text">Caricamento risultati...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <h2 class="error-title">Errore</h2>
      <p class="error-text">{{ error }}</p>
      <button class="back-button" @click="goBack">
        Torna alla Dashboard
      </button>
    </div>

    <!-- Processing State -->
    <div v-else-if="analysis && analysis.status !== 'completed'" class="processing-container">
      <h2 class="processing-title">Analisi in Corso</h2>
      <StatusBadge :status="analysis.status" />
      <p class="processing-text">
        L'analisi per <strong>{{ analysis.company_name }}</strong> è ancora in elaborazione.
        <br />
        Torna più tardi per vedere i risultati.
      </p>
      <button class="back-button" @click="goBack">
        Torna alla Dashboard
      </button>
    </div>

    <!-- Results Display -->
    <div v-else-if="analysis && analysis.status === 'completed'" class="results-container">
      <!-- Back Button -->
      <div class="back-button-container">
        <button class="back-button" @click="goBack">
          ← Torna alla Dashboard
        </button>
      </div>

      <!-- Title Section -->
      <div class="title-section">
        <h2 class="page-title">Risultati Finali</h2>
        <h3 class="company-subtitle">
          Analisi per <span class="company-name">{{ analysis.company_name }}</span>
        </h3>
      </div>

      <!-- Content: Radar + Scores -->
      <div class="content">
        <!-- Radar Chart -->
        <div class="radar-section">
          <RadarGraph
            ref="spiderGraphRef"
            :height="500"
            :width="500"
            :numberOfDataPoints="3"
            :legend="['Community', 'Storytelling', 'Experience']"
            :dataRatingsColors="[
              'rgba(46, 78, 237, 0.2)',
              'rgba(46, 78, 237, 0.2)',
              'rgba(46, 78, 237, 0.2)',
            ]"
            :dataRatingsGridColors="[
              'rgba(46, 78, 237, 0.2)',
              'rgba(46, 78, 237, 0.2)',
              'rgba(46, 78, 237, 0.2)',
            ]"
          />
        </div>

        <!-- Scores Section -->
        <div class="scores-section">
          <!-- Community Score -->
          <div class="score-box">
            <div class="score-title">Community</div>
            <div class="score-bar-container">
              <div class="score-bar">
                <div
                  class="score-fill"
                  :style="{ width: communityScore * 10 + '%' }"
                ></div>
              </div>
              <div class="score">{{ communityScore * 10 }} / 100</div>
            </div>
            <!-- Community Sub-scores -->
            <div class="sub-scores">
              <div class="sub-score-item">
                <div class="sub-score-label">Tasso di Coinvolgimento</div>
                <div class="sub-score-bar-container">
                  <div class="sub-score-bar">
                    <div
                      class="sub-score-fill"
                      :style="{ width: commEngageRate * 100 + '%' }"
                    ></div>
                  </div>
                  <div class="sub-score-value">{{ (commEngageRate * 100).toFixed(1) }}%</div>
                </div>
              </div>
              <div class="sub-score-item">
                <div class="sub-score-label">Frequenza Post Taggati</div>
                <div class="sub-score-bar-container">
                  <div class="sub-score-bar">
                    <div
                      class="sub-score-fill"
                      :style="{ width: commTaggedTimeDiff * 100 + '%' }"
                    ></div>
                  </div>
                  <div class="sub-score-value">{{ (commTaggedTimeDiff * 100).toFixed(1) }}%</div>
                </div>
              </div>
              <div class="sub-score-item">
                <div class="sub-score-label">Mediana Like</div>
                <div class="sub-score-bar-container">
                  <div class="sub-score-bar">
                    <div
                      class="sub-score-fill"
                      :style="{ width: commLikeMedian * 100 + '%' }"
                    ></div>
                  </div>
                  <div class="sub-score-value">{{ (commLikeMedian * 100).toFixed(1) }}%</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Experience Score -->
          <div class="score-box">
            <div class="score-title">Experience</div>
            <div class="score-bar-container">
              <div class="score-bar">
                <div
                  class="score-fill"
                  :style="{ width: experienceScore * 10 + '%' }"
                ></div>
              </div>
              <div class="score">{{ experienceScore * 10 }} / 100</div>
            </div>
            <!-- Experience Sub-scores -->
            <div class="sub-scores">
              <div class="sub-score-item">
                <div class="sub-score-label">Funzionalità Sito Web</div>
                <div class="sub-score-bar-container">
                  <div class="sub-score-bar">
                    <div
                      class="sub-score-fill"
                      :style="{ width: (expFlagsScore / 12) * 100 + '%' }"
                    ></div>
                  </div>
                  <div class="sub-score-value">{{ expFlagsScore }} / 12</div>
                </div>
              </div>
              <div class="sub-score-item">
                <div class="sub-score-label">Recensioni Google Maps</div>
                <div class="sub-score-bar-container">
                  <div class="sub-score-bar">
                    <div
                      class="sub-score-fill"
                      :style="{ width: (expGoogleMapsReview / 5) * 100 + '%' }"
                    ></div>
                  </div>
                  <div class="sub-score-value">{{ expGoogleMapsReview.toFixed(1) }} / 5</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Storytelling Score -->
          <div class="score-box">
            <div class="score-title">Storytelling</div>
            <div class="score-bar-container">
              <div class="score-bar">
                <div
                  class="score-fill"
                  :style="{ width: storytellingScore * 10 + '%' }"
                ></div>
              </div>
              <div class="score">{{ storytellingScore * 10 }} / 100</div>
            </div>
            <!-- Storytelling Sub-scores -->
            <div class="sub-scores">
              <div class="sub-score-item">
                <div class="sub-score-label">Frequenza Post</div>
                <div class="sub-score-bar-container">
                  <div class="sub-score-bar">
                    <div
                      class="sub-score-fill"
                      :style="{ width: stlgFrequencyScore * 10 + '%' }"
                    ></div>
                  </div>
                  <div class="sub-score-value">{{ stlgFrequencyScore.toFixed(1) }} / 10</div>
                </div>
              </div>
              <div class="sub-score-item">
                <div class="sub-score-label">Punteggio Condivisioni</div>
                <div class="sub-score-bar-container">
                  <div class="sub-score-bar">
                    <div
                      class="sub-score-fill"
                      :style="{ width: stlgShareScore * 10 + '%' }"
                    ></div>
                  </div>
                  <div class="sub-score-value">{{ stlgShareScore.toFixed(1) }} / 10</div>
                </div>
              </div>
              <div class="sub-score-item">
                <div class="sub-score-label">Coerenza Contenuti (AI)</div>
                <div class="sub-score-bar-container">
                  <div class="sub-score-bar">
                    <div
                      class="sub-score-fill"
                      :style="{ width: stlgCoherenceScore * 10 + '%' }"
                    ></div>
                  </div>
                  <div class="sub-score-value">{{ stlgCoherenceScore.toFixed(1) }} / 10</div>
                </div>
              </div>
              <!-- AI Reasoning -->
              <div v-if="stlgReasoning" class="sub-score-reasoning">
                <button
                  class="reasoning-toggle"
                  @click="showReasoning = !showReasoning"
                >
                  {{ showReasoning ? 'Nascondi' : 'Mostra' }} Analisi AI
                  <span class="reasoning-arrow" :class="{ open: showReasoning }">&#9662;</span>
                </button>
                <div v-if="showReasoning" class="reasoning-text">
                  {{ stlgReasoning }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAnalysisStore } from '@/stores/analysisStore';
import { useSessionStore } from '@/stores/sessionStore';
import { useAuthStore } from '@/stores/authStore';
import RadarGraph from '@/components/widgets/radar.vue';
import StatusBadge from './StatusBadge.vue';

export default {
  name: 'AnalisiResults',
  components: {
    RadarGraph,
    StatusBadge,
  },
  setup() {
    const analysisStore = useAnalysisStore();
    const sessionStore = useSessionStore();
    return { analysisStore, sessionStore };
  },
  data() {
    return {
      loading: true,
      error: null,
      analysis: null,
      userId: null,
      showReasoning: false,
    };
  },
  computed: {
    communityScore() {
      return this.analysis?.community_score?.community_score || 0;
    },
    experienceScore() {
      return this.analysis?.experience_score?.experience_score || 0;
    },
    storytellingScore() {
      return this.analysis?.storytelling_score?.storytelling_score || 0;
    },
    // Community sub-scores (0-1 scale)
    commEngageRate() {
      return this.analysis?.community_score?.comm_engage_rate_benchmark || 0;
    },
    commTaggedTimeDiff() {
      return this.analysis?.community_score?.comm_tagged_time_diff_score || 0;
    },
    commLikeMedian() {
      return this.analysis?.community_score?.comm_like_median_score || 0;
    },
    // Storytelling sub-scores (0-10 scale)
    stlgFrequencyScore() {
      return this.analysis?.storytelling_score?.stlg_frequency_score || 0;
    },
    stlgShareScore() {
      return this.analysis?.storytelling_score?.stlg_share_score || 0;
    },
    stlgCoherenceScore() {
      return this.analysis?.storytelling_score?.stlg_coherence_score || 0;
    },
    stlgReasoning() {
      return this.analysis?.storytelling_score?.stlg_reasoning || '';
    },
    // Experience sub-scores
    expFlagsScore() {
      return this.analysis?.experience_score?.exp_flags_score || 0;
    },
    expGoogleMapsReview() {
      return this.analysis?.experience_score?.exp_google_maps_review || 0;
    },
  },
  async mounted() {
    // Get user ID
    const authStore = useAuthStore();
    const user = authStore.user;
    if (!user) {
      this.$router.push('/login');
      return;
    }

    this.userId = user.id;

    // Get analysis ID from route
    const analysisId = this.$route.params.id;

    if (!analysisId) {
      this.error = 'ID analisi mancante';
      this.loading = false;
      return;
    }

    // Fetch analysis
    await this.fetchAnalysis(analysisId);
  },
  methods: {
    async fetchAnalysis(analysisId) {
      try {
        this.loading = true;
        this.error = null;

        const data = await this.analysisStore.fetchAnalysisById(
          analysisId,
          this.userId
        );

        this.analysis = data;

        // If completed, populate session store for radar chart
        if (data.status === 'completed') {
          this.populateSessionStore(data);
        }

        this.loading = false;
      } catch (error) {
        console.error('Error fetching analysis:', error);
        this.error = error.message || 'Impossibile caricare l\'analisi';
        this.loading = false;
      }
    },

    populateSessionStore(analysis) {
      // Set company name
      this.sessionStore.company_name = analysis.company_name;

      // Set scores
      if (analysis.community_score) {
        this.sessionStore.setScore('community', analysis.community_score);
      }

      if (analysis.storytelling_score) {
        this.sessionStore.setScore('storytelling', analysis.storytelling_score);
      }

      if (analysis.experience_score) {
        this.sessionStore.setScore('experience', analysis.experience_score);
      }
    },

    goBack() {
      this.$router.push('/analisi');
    },
  },
};
</script>

<style scoped>
.results-page {
  position: relative;
  min-height: 100vh;
  padding: 2rem;
  padding-top: 120px; /* Account for fixed navbar */
  background: linear-gradient(135deg, #ffffff, #56bcee);
}

.loading-container,
.error-container,
.processing-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1.5rem;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.loading-gif {
  width: 60px;
  height: 60px;
}

.loading-text,
.processing-text {
  font-size: 1.125rem;
  color: #1a1a1a;
  line-height: 1.6;
}

.error-title,
.processing-title {
  font-size: 2rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.error-text {
  font-size: 1.125rem;
  color: #ef4444;
  margin: 0;
}

.back-button {
  background: #2e4eed;
  border: 2px solid #2e4eed;
  color: white;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-button:hover {
  background: #1e3ed8;
  border-color: #1e3ed8;
}

.results-container {
  max-width: 1600px;
  margin: 0 auto;
}

.back-button-container {
  margin-bottom: 2rem;
}

.title-section {
  width: 100%;
  margin-bottom: 3rem;
  text-align: left;
}

.page-title {
  font-size: 3rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.company-subtitle {
  font-size: 2rem;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0;
}

.company-name {
  color: #2e4eed;
}

.content {
  display: flex;
  gap: 6rem;
  width: 100%;
}

.radar-section {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.scores-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.score-box {
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid #2e4eed;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.score-title {
  font-size: 1.8rem;
  font-weight: 500;
  color: #1a1a1a;
}

.score-bar-container {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.score-bar {
  flex: 1;
  height: 2rem;
  background-color: rgba(46, 78, 237, 0.1);
  border: 2px solid #2e4eed;
  overflow: hidden;
}

.score-fill {
  height: 100%;
  background-color: #2e4eed;
  transition: width 1s ease-in-out;
}

.score {
  font-size: 2.5rem;
  font-weight: 600;
  color: #2e4eed;
  min-width: 100px;
  text-align: right;
}

/* Sub-scores */
.sub-scores {
  border-top: 1px solid rgba(46, 78, 237, 0.2);
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.sub-score-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.sub-score-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #4a4a4a;
}

.sub-score-bar-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.sub-score-bar {
  flex: 1;
  height: 0.75rem;
  background-color: rgba(46, 78, 237, 0.08);
  border: 1px solid rgba(46, 78, 237, 0.3);
  border-radius: 4px;
  overflow: hidden;
}

.sub-score-fill {
  height: 100%;
  background-color: #2e4eed;
  transition: width 1s ease-in-out;
  border-radius: 3px;
}

.sub-score-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2e4eed;
  min-width: 70px;
  text-align: right;
  white-space: nowrap;
}

/* AI Reasoning toggle */
.sub-score-reasoning {
  margin-top: 0.25rem;
}

.reasoning-toggle {
  background: none;
  border: 1px solid rgba(46, 78, 237, 0.3);
  color: #2e4eed;
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border-radius: 4px;
}

.reasoning-toggle:hover {
  background: rgba(46, 78, 237, 0.06);
  border-color: #2e4eed;
}

.reasoning-arrow {
  display: inline-block;
  font-size: 0.7rem;
  transition: transform 0.2s ease;
}

.reasoning-arrow.open {
  transform: rotate(180deg);
}

.reasoning-text {
  margin-top: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(46, 78, 237, 0.04);
  border-left: 3px solid #2e4eed;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #333;
  border-radius: 0 4px 4px 0;
}

@media (max-width: 1024px) {
  .content {
    flex-direction: column;
    align-items: center;
    gap: 3rem;
  }

  .radar-section,
  .scores-section {
    width: 100%;
    max-width: 600px;
  }
}

@media (max-width: 768px) {
  .results-page {
    padding: 1rem;
    padding-top: 100px; /* Account for fixed navbar on mobile */
  }

  .page-title {
    font-size: 2rem;
  }

  .company-subtitle {
    font-size: 1.5rem;
  }

  .score-box {
    padding: 1.25rem;
  }

  .score-title {
    font-size: 1.5rem;
  }

  .score {
    font-size: 2rem;
    min-width: 80px;
  }

  .sub-score-label {
    font-size: 0.85rem;
  }

  .sub-score-value {
    font-size: 0.85rem;
    min-width: 60px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.75rem;
  }

  .company-subtitle {
    font-size: 1.25rem;
  }

  .score-title {
    font-size: 1.25rem;
  }

  .score {
    font-size: 1.75rem;
  }

  .sub-score-label {
    font-size: 0.8rem;
  }

  .sub-score-value {
    font-size: 0.8rem;
    min-width: 55px;
  }

  .sub-score-bar {
    height: 0.6rem;
  }

  .reasoning-text {
    font-size: 0.8rem;
  }
}
</style>
